/**
 * DocumentPreviewViewElement — kreator formatek: klocek palety reprezentujący
 * panel podglądu dokumentu/pliku rekordu (`document_preview_view`, patrz
 * `RecursiveFormRenderer` PLACEHOLDER_TYPES).
 *
 * Mirror `DocumentItemsViewElement` (patrz nagłówek tamtego pliku) — na
 * REALNEJ stronie rekordu ten typ jest dispatchowany WYŁĄCZNIE przez
 * `contextRenderers['document_preview_view']` → `buildDocumentPreviewContextRenderer`
 * (`apps/admin/src/components/attachments/DocumentPreviewContextRenderer.tsx`,
 * widget `DocumentPreviewPanel` — poza zakresem tego rejestru, świadomie
 * jeszcze niepodpięty do żadnej strony hosta). Ten plik obsługuje TYLKO
 * podgląd w canvasie buildera (mode='builder'), gdzie nie ma prawdziwego
 * rekordu do wyświetlenia, plus `propertiesEditor` konfiguracji ŹRÓDŁA
 * załączników (`settings.source`/`settings.fieldSlug`, patrz
 * `preview-source.ts` po pełną semantykę czterech trybów).
 *
 * `allowedModules` scopinguje ten klocek do hostów tej iteracji — lista wydzielona
 * do `./attachment-host-modules.ts` (dzielona z `AttachmentsViewElement`, który ma
 * IDENTYCZNY zakres hostów — patrz nagłówek tamtego pliku).
 *
 * `render`/`propertiesEditor` są wołane jak ZWYKŁE FUNKCJE (nie komponenty) —
 * patrz `LayoutElementRegistry.render`/`propertiesEditor` — więc żaden hook nie
 * może być wywołany BEZPOŚREDNIO w ich ciele (ten sam powód co w
 * `DocumentItemsViewElement`): `i18n.t` standalone w `render`, a
 * `propertiesEditor` montuje `PreviewSourceEditor` (prawdziwy komponent, hooki
 * bezpieczne wewnątrz jego cyklu życia — mirror `DocumentItemsColumnsCanvas`, osobnego pliku
 * montowanego przez `render` klocka `DocumentItemsViewElement`).
 * `PreviewSourceEditor` czyta moduleSlug z `useParams` — bezpieczne, bo
 * `propertiesEditor` jest montowany WEWNĄTRZ `SystemModuleBuilderPage`, trasa
 * `/system-modules/:slug/builder`, ten sam slug co `allowedModules` powyżej.
 */

import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Eye } from 'lucide-react';

import {
  type DocumentPreviewSourceMode,
  readPreviewSourceSettings,
} from '@/components/attachments/preview-source';
import { usePreviewSourceFieldOptions } from '@/components/attachments/use-preview-source-fields';
import { Dropdown } from '@/components/Dropdown';
import type { LayoutElementDefinition } from '@/components/fields/LayoutElementRegistry';
import { FormField } from '@/components/FormField';
import i18n from '@/i18n';

import { ATTACHMENT_HOST_ALLOWED_MODULES } from './attachment-host-modules';

const SOURCE_OPTION_ORDER: DocumentPreviewSourceMode[] = ['all', 'primary', 'latest', 'field'];

/** Klucz i18n + fallback PL per tryb źródła — współdzielone przez editor (canvas
 *  buildera, `useTranslation`) i `render` (bare function, `i18n.t` standalone). */
const SOURCE_LABEL_I18N: Record<DocumentPreviewSourceMode, { key: string; defaultValue: string }> =
  {
    all: {
      key: 'attachmentPreview.propertiesEditor.sourceAll',
      defaultValue: 'Wszystkie załączniki',
    },
    primary: {
      key: 'attachmentPreview.propertiesEditor.sourcePrimary',
      defaultValue: 'Załącznik główny',
    },
    latest: {
      key: 'attachmentPreview.propertiesEditor.sourceLatest',
      defaultValue: 'Najnowszy załącznik',
    },
    field: {
      key: 'attachmentPreview.propertiesEditor.sourceField',
      defaultValue: 'Wartość pola dodatkowego',
    },
  };

/**
 * Editor właściwości źródła — komponent PRAWDZIWY (montowany, nie wołany jak
 * funkcja), więc `useParams`/`useTranslation`/`usePreviewSourceFieldOptions`
 * (React Query) są bezpieczne. `onChange` scala PARTIAL settings (kontrakt
 * `LayoutElementPropertiesEditorProps`) — zmiana `source` na coś innego niż
 * `field` czyści `fieldSlug` (nie zostawiamy martwej referencji do pola z
 * poprzedniego wyboru źródła).
 */
function PreviewSourceEditor({
  settings,
  onChange,
}: {
  settings: Record<string, unknown>;
  onChange: (patch: Record<string, unknown>) => void;
}) {
  const { t } = useTranslation();
  const { slug: moduleSlug } = useParams<{ slug: string }>();
  const { source, fieldSlug } = readPreviewSourceSettings(settings);
  const { options: fieldOptions, isLoading: fieldOptionsLoading } =
    usePreviewSourceFieldOptions(moduleSlug);

  const sourceLabels: Record<DocumentPreviewSourceMode, string> = {
    all: t(SOURCE_LABEL_I18N.all.key, { defaultValue: SOURCE_LABEL_I18N.all.defaultValue }),
    primary: t(SOURCE_LABEL_I18N.primary.key, {
      defaultValue: SOURCE_LABEL_I18N.primary.defaultValue,
    }),
    latest: t(SOURCE_LABEL_I18N.latest.key, {
      defaultValue: SOURCE_LABEL_I18N.latest.defaultValue,
    }),
    field: t(SOURCE_LABEL_I18N.field.key, { defaultValue: SOURCE_LABEL_I18N.field.defaultValue }),
  };

  // Backend nie ma dziś pól file/image/gallery na hostach tego widgetu (patrz
  // nagłówek `use-preview-source-fields.ts`) — dopóki lista jest pusta, opcja
  // źródła „field” jest disabled w select-u (nie usuwamy mechanizmu, tylko go
  // blokujemy, żeby nie wybrać źródła bez żadnego pola do wskazania).
  const fieldSourceUnavailable = !fieldOptionsLoading && fieldOptions.length === 0;

  return (
    <div className="space-y-3">
      <FormField
        label={t('attachmentPreview.propertiesEditor.sourceLabel', {
          defaultValue: 'Źródło załącznika',
        })}
        hint={
          fieldSourceUnavailable
            ? t('attachmentPreview.propertiesEditor.sourceFieldUnavailableHint', {
                defaultValue:
                  'Opcja „Wartość pola dodatkowego” jest niedostępna — brak pól typu plik/obraz/galeria w tym module.',
              })
            : undefined
        }
      >
        <Dropdown
          value={source}
          onChange={(v) => {
            const next = v as DocumentPreviewSourceMode;
            onChange(next === 'field' ? { source: next } : { source: next, fieldSlug: null });
          }}
          options={SOURCE_OPTION_ORDER.map((mode) => {
            const disabled = mode === 'field' && fieldSourceUnavailable;
            const label = disabled
              ? `${sourceLabels[mode]} ${t('attachmentPreview.propertiesEditor.sourceFieldUnavailableSuffix', { defaultValue: '(niedostępne)' })}`
              : sourceLabels[mode];
            return { value: mode, label, disabled };
          })}
          data-testid="document-preview-source-select"
        />
      </FormField>

      {source === 'field' && (
        <FormField
          label={t('attachmentPreview.propertiesEditor.fieldLabel', {
            defaultValue: 'Pole (plik / obraz / galeria)',
          })}
          hint={
            !fieldOptionsLoading && fieldOptions.length === 0
              ? t('attachmentPreview.propertiesEditor.noFileFields', {
                  defaultValue: 'Brak pól typu plik/obraz/galeria w tym module.',
                })
              : undefined
          }
        >
          <Dropdown
            value={fieldSlug ?? ''}
            onChange={(v) => onChange({ fieldSlug: v || null })}
            options={[
              { value: '', label: '—' },
              ...fieldOptions.map((f) => ({ value: f.slug, label: f.name })),
            ]}
            disabled={fieldOptionsLoading || fieldOptions.length === 0}
            data-testid="document-preview-source-field-select"
          />
        </FormField>
      )}
    </div>
  );
}

export const DocumentPreviewViewElement: LayoutElementDefinition = {
  type: 'document_preview_view',
  category: 'tresc',
  isContainer: false,
  label: 'Podgląd dokumentu',
  icon: 'Eye',
  allowedModules: ATTACHMENT_HOST_ALLOWED_MODULES,
  defaultSettings: () => ({ source: 'all', fieldSlug: null }),
  propertiesEditor: ({ settings, onChange }) => (
    <PreviewSourceEditor settings={settings} onChange={onChange} />
  ),
  render: ({ element }) => {
    const { source } = readPreviewSourceSettings(element.settings);
    return (
      <div
        className="rounded-lg border border-dashed border-border-secondary bg-bg-secondary p-4 text-center"
        data-testid={`document-preview-view-element-${element.id}`}
        data-element-type="document_preview_view"
      >
        <Eye className="mx-auto h-5 w-5 text-text-muted" aria-hidden="true" />
        <p className="mt-1 text-xs font-medium text-text-secondary">
          {i18n.t('attachmentPreview.paletteLabel', { defaultValue: 'Podgląd dokumentu' })}
        </p>
        <p className="mt-0.5 text-[10px] text-text-muted">
          {i18n.t(SOURCE_LABEL_I18N[source].key, {
            defaultValue: SOURCE_LABEL_I18N[source].defaultValue,
          })}
        </p>
      </div>
    );
  },
};
